// Shared HTTP layer: version header, request signature, mapping responses onto
// typed errors, and retry timing.
//
// Every request a browser makes here is cross-origin (company servers and the
// directory live on their own domains), so everything below assumes the servers
// meet the CORS obligations of conventions §12 — a server that does not is
// indistinguishable from one that is down, and surfaces as `no_network`.

import { utf8 } from '$lib/crypto/hash';
import { signatureHeader } from '$lib/crypto/signer';
import { config } from '$lib/config';

import { ClientError, errorCodeFromName, errorCodeFromStatus } from './errors';

export interface ApiResult {
  status: number;
  body: Record<string, unknown>;
}

/**
 * Joins a base URL with an endpoint path, keeping any path prefix the base
 * carries. A bare origin (`https://host`) gives `/v1/…`; a prefixed base
 * (`https://host/functions/v1/imp`) gives `/functions/v1/imp/v1/…`.
 *
 * The prefix matters: a directory entry's `server_url` is a URI, not a host, so a
 * company may serve the protocol from a subpath of a shared domain. The signature
 * covers this full pathname; the query string is not signed.
 */
export function joinPath(base: string, path: string, query?: Record<string, string>): URL {
  const url = new URL(base);
  const prefix = url.pathname.endsWith('/') ? url.pathname.slice(0, -1) : url.pathname;
  url.pathname = prefix + path;
  url.search = '';
  if (query !== undefined) {
    for (const [key, value] of Object.entries(query)) {
      url.searchParams.set(key, value);
    }
  }
  url.hash = '';
  return url;
}

export interface SendInput {
  method: string;
  url: URL;
  body?: Record<string, unknown>;
  bytes?: Uint8Array;
  contentType?: string;
  token?: string;
  /** Sign with this account's device key. */
  signingAccountId?: string;
  /** Extra request headers, e.g. the entry gate's device-signature pair. */
  headers?: Record<string, string>;
  timeoutMs?: number;
  /** External abort — the held events request is cancelled through this. */
  signal?: AbortSignal;
}

function combinedSignal(timeoutMs: number, external: AbortSignal | undefined): AbortSignal {
  const timeout = AbortSignal.timeout(timeoutMs);
  return external === undefined ? timeout : AbortSignal.any([timeout, external]);
}

export async function send(input: SendInput): Promise<ApiResult> {
  const bodyBytes =
    input.bytes ?? (input.body === undefined ? new Uint8Array(0) : utf8(JSON.stringify(input.body)));

  const headers: Record<string, string> = {
    'X-Imp-Version': `${config.protocolVersion}`,
    Accept: 'application/json',
    ...input.headers,
  };
  if (bodyBytes.length > 0) {
    headers['Content-Type'] = input.contentType ?? 'application/json; charset=utf-8';
  }
  if (input.token !== undefined) {
    headers['Authorization'] = `Bearer ${input.token}`;
  }
  if (input.signingAccountId !== undefined) {
    headers['X-Imp-Signature'] = await signatureHeader({
      accountId: input.signingAccountId,
      method: input.method,
      path: input.url.pathname,
      body: bodyBytes,
    });
  }

  let response: Response;
  try {
    response = await fetch(input.url, {
      method: input.method,
      headers,
      ...(bodyBytes.length > 0 ? { body: bodyBytes as BodyInit } : {}),
      signal: combinedSignal(input.timeoutMs ?? 20_000, input.signal),
    });
  } catch (error) {
    // Timeouts, dropped connections, CORS refusals and external aborts all land
    // here — the browser wraps them alike, and the caller's own state decides
    // whether an abort was wanted.
    throw new ClientError('no_network', {
      message: error instanceof Error ? error.message : 'network failure',
    });
  }

  let decoded: Record<string, unknown> = {};
  const text = await response.text();
  if (text.length > 0) {
    const type = response.headers.get('content-type');
    if (type === null || type.includes('json')) {
      try {
        const json: unknown = JSON.parse(text);
        if (typeof json === 'object' && json !== null && !Array.isArray(json)) {
          decoded = json as Record<string, unknown>;
        }
      } catch {
        if (response.status < 400) {
          throw new ClientError('unreadable_response');
        }
      }
    }
  }

  if (response.status >= 400) {
    const rawCode = decoded['code'];
    const code = typeof rawCode === 'string' ? errorCodeFromName(rawCode) : errorCodeFromStatus(response.status);
    const retry = decoded['retry_after_s'];
    const error = new ClientError(code === 'unknown' ? errorCodeFromStatus(response.status) : code, {
      ...(typeof decoded['message'] === 'string' ? { message: decoded['message'] as string } : {}),
      ...(Number.isInteger(retry) ? { retryAfterS: retry as number } : {}),
    });
    // One line per failed call, path only — enough to diagnose a deployment from
    // the console without exposing tokens or bodies.
    console.info(`[api] ${input.method} ${input.url.pathname} -> HTTP ${response.status} ${error.code}`);
    throw error;
  }

  return { status: response.status, body: decoded };
}

/** Raw bytes — photos and recordings do not go through the JSON decoder. */
export async function getBytes(input: {
  url: URL;
  token?: string;
  signingAccountId?: string;
  timeoutMs?: number;
  signal?: AbortSignal;
}): Promise<Uint8Array> {
  const headers: Record<string, string> = {
    'X-Imp-Version': `${config.protocolVersion}`,
  };
  if (input.token !== undefined) {
    headers['Authorization'] = `Bearer ${input.token}`;
  }
  if (input.signingAccountId !== undefined) {
    headers['X-Imp-Signature'] = await signatureHeader({
      accountId: input.signingAccountId,
      method: 'GET',
      path: input.url.pathname,
      body: new Uint8Array(0),
    });
  }
  let response: Response;
  try {
    response = await fetch(input.url, {
      method: 'GET',
      headers,
      signal: combinedSignal(input.timeoutMs ?? 30_000, input.signal),
    });
  } catch (error) {
    throw new ClientError('no_network', {
      message: error instanceof Error ? error.message : 'network failure',
    });
  }
  if (response.status >= 400) {
    throw new ClientError(errorCodeFromStatus(response.status));
  }
  return new Uint8Array(await response.arrayBuffer());
}

/** Exponential backoff with jitter. The jitter matters: without it every client
 *  that lost connectivity at the same moment would come back to the server
 *  together and recreate the outage they waited out. */
export function retryDelayMs(attempt: number): number {
  const clamped = Math.min(Math.max(attempt, 0), 8);
  const baseMs = 1000 * 2 ** clamped;
  const jitter = Math.floor(Math.random() * (baseMs / 2 + 1));
  const maxMs = 6 * 60 * 60 * 1000;
  return Math.min(Math.max(baseMs + jitter, 1000), maxMs);
}
